import type { GetSurveyResult } from './api'
import type { SurveyContent } from './contract'
import type { PulseEmptyDetail } from './events'

// 'no-config' is the one empty reason a response can never carry: it means the slot had no serve
// target, which is known before a request goes out. Excluding it here makes that a type error
// rather than a comment.
export type ServeEmptyReason = Exclude<PulseEmptyDetail['reason'], 'no-config'>

export type ServeOutcome
  = | { kind: 'survey', survey: SurveyContent }
    | { kind: 'empty', reason: ServeEmptyReason }

/**
 * What a serve response means, as one decision with nothing else attached — no phase, no collapse,
 * no event. The element owns those; this owns only the reading.
 *
 * The four answers, in the order they are ruled out:
 *
 * - no-fill — the backend has nothing to serve this brand/category right now. Both ways of saying
 *   so (204 and 404) reach here as the no-content arm — api.ts folds them together — so this
 *   decision never reads a status code. The normal quiet outcome, not a failure.
 * - error — the fetch failed, the server failed, or the payload could not be read. Collapses the
 *   same way no-fill does; an integrator tells them apart by the reason alone.
 * - no-questions — a survey came back but every question in it was of a type this build does not
 *   know, so the tolerant reader in api.ts filtered them all out. An old embed on a CDN must go
 *   quiet when the backend ships a new question type, not render an empty shell.
 * - survey — there is something to show.
 */
export function serveOutcome(result: GetSurveyResult): ServeOutcome {
  const { data, error } = result
  if (!data && !error)
    return { kind: 'empty', reason: 'no-fill' }
  if (error || !data)
    return { kind: 'empty', reason: 'error' }
  if (data.questions.length === 0)
    return { kind: 'empty', reason: 'no-questions' }
  return { kind: 'survey', survey: data }
}
